import * as Localization from 'expo-localization';
import { useEffect, useId, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { Pressable, ScrollView, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Button, HStack, Text, VStack, theme } from '@squire/ui';

import { useCookieConsentSingleton } from '@/context/cookieConsent';
import { useLegalBottomSheet } from '@/context/legal';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { writeConsent } from '@/utils/cookieConsentStorage';
import { Logger } from '@/utils/logging';
import { Tracker } from '@/utils/tracking';

import { CookieConsentDetails } from '../CookieConsentDetails';
import { FOOTER_HEIGHT, HEADER_HEIGHT, styles } from './styles';
import type { CookieConsentProps } from './types';

const CookieConsent = ({
  position = 'absolute',
  style,
}: CookieConsentProps) => {
  const instanceId = useId();
  const {
    registerInstance,
    unregisterInstance,
    isActiveInstance,
    pendingResponse,
    setPendingResponse,
  } = useCookieConsentSingleton();
  const { openPrivacy } = useLegalBottomSheet();
  const { t } = useScreenTranslations('CookieConsent');

  const contentHeight = useSharedValue(0);

  const { regionCode } = Localization.getLocales()[0];

  const isNorthAmerica = regionCode === 'US' || regionCode === 'CA';
  const [showFull, setShowFull] = useState(false);
  const [selectedTab, setSelectedTab] = useState<
    'consent' | 'details' | 'about'
  >('consent');

  const [selectedCategories, setSelectedCategories] = useState({
    necessary: true,
    statistics: false,
  });

  const rStyle = useAnimatedStyle(() => {
    const containerHeight =
      Math.min(contentHeight.value, 600) +
      HEADER_HEIGHT +
      FOOTER_HEIGHT +
      theme.spacing[6] * 2;

    return {
      height: withTiming(containerHeight, { duration: 300 }),
    };
  });

  useEffect(() => {
    registerInstance(instanceId);
    return () => {
      unregisterInstance(instanceId);
    };
  }, [instanceId, registerInstance, unregisterInstance]);

  styles.useVariants({ position });

  if (!isActiveInstance(instanceId)) {
    return null;
  }

  const handleLayout = (event: LayoutChangeEvent) => {
    contentHeight.value = event.nativeEvent.layout.height;
  };

  const handleCategoryToggle = (categoryId: string, enabled: boolean) => {
    setSelectedCategories((prev: typeof selectedCategories) => ({
      ...prev,
      [categoryId]: enabled,
    }));
  };

  const saveConsentIntoStorage = (
    necessary: boolean = selectedCategories.necessary,
    statistics: boolean = selectedCategories.statistics,
  ) => {
    Logger.info('Cookie consent saved', { necessary, statistics });
    writeConsent({ necessary, statistics });
    setPendingResponse(false);
  };

  const handleAllowAll = () => {
    Tracker.track('cookie_consent', {
      consented: 'true',
      statistics: 'true',
    });
    saveConsentIntoStorage(true, true);
  };

  const handleAllowSelection = () => {
    Tracker.track('cookie_consent', {
      consented: 'true',
      statistics: String(selectedCategories.statistics),
    });
    saveConsentIntoStorage();
  };

  const handleDeny = () => {
    Tracker.track('cookie_consent', {
      consented: 'true',
      statistics: 'false',
    });
    saveConsentIntoStorage(true, false);
  };

  const handleReject = () => {
    Tracker.track('cookie_consent', {
      consented: 'false',
      statistics: 'false',
    });
    setPendingResponse(false);
  };

  const handlePrimaryButtonPress = () => {
    if (selectedTab === 'details') {
      handleAllowSelection();
    } else if (isNorthAmerica) {
      setSelectedTab('details');
    } else {
      handleAllowAll();
    }
  };

  if (!pendingResponse) return null;
  if (isNorthAmerica && !showFull) {
    return (
      <HStack
        style={styles.container}
        paddingHorizontal={theme.spacing[6]}
        paddingVertical={theme.spacing[4]}
        centerVertical
      >
        <VStack flex space={theme.spacing[2]}>
          <Text color="textPrimaryInverse" variant="mStrong">
            {t('thisSiteUsesCookie')}
          </Text>
          <Text color="textPrimaryInverse" variant="s">
            {t('manageYourPrivacyChoices')}{' '}
            <Pressable onPress={() => setShowFull(true)}>
              <Text
                variant="s"
                color="textPrimaryInverse"
                style={styles.underlineText}
              >
                {t('here')}
              </Text>
            </Pressable>
          </Text>
        </VStack>

        <View style={styles.buttonsContainer}>
          <Button size="small" variant="secondary" onPress={handleAllowAll}>
            <Text color="textPrimaryInverse">{t('accept')}</Text>
          </Button>
          <Button size="small" variant="secondary" onPress={handleReject}>
            <Text color="textPrimaryInverse">{t('rejectButton')}</Text>
          </Button>
        </View>
      </HStack>
    );
  }
  return (
    <Animated.View style={[rStyle, styles.container, style]}>
      <HStack style={styles.tabsHeader}>
        <Pressable
          onPress={() => setSelectedTab('consent')}
          style={[
            styles.tabButton,
            selectedTab === 'consent' && styles.tabButtonSelected,
          ]}
        >
          <Text color="textPrimaryInverse">{t('consentTab')}</Text>
        </Pressable>
        <Pressable
          onPress={() => setSelectedTab('details')}
          style={[
            styles.tabButton,
            selectedTab === 'details' && styles.tabButtonSelected,
          ]}
        >
          <Text color="textPrimaryInverse">{t('detailsTab')}</Text>
        </Pressable>
        <Pressable
          onPress={() => setSelectedTab('about')}
          style={[
            styles.tabButton,
            selectedTab === 'about' && styles.tabButtonSelected,
          ]}
        >
          <Text color="textPrimaryInverse">{t('aboutTab')}</Text>
        </Pressable>
      </HStack>

      <VStack paddingHorizontal={theme.spacing[6]} space={theme.spacing[6]}>
        <ScrollView
          onLayout={handleLayout}
          style={styles.resizableContainer}
          showsVerticalScrollIndicator={false}
        >
          {selectedTab === 'consent' && (
            <Text variant="s" color="textPrimaryInverse">
              {t('consentContent')}
            </Text>
          )}

          {selectedTab === 'details' && (
            <CookieConsentDetails
              selectedCategories={selectedCategories}
              handleCategoryToggle={handleCategoryToggle}
            />
          )}

          {selectedTab === 'about' && (
            <Text variant="s" color="textPrimaryInverse">
              {t('aboutContent')}{' '}
              <Text
                onPress={openPrivacy}
                style={styles.underlineText}
                color="textPrimaryInverse"
                variant="s"
              >
                {t('privacyPolicyLink')}
              </Text>
            </Text>
          )}
        </ScrollView>

        <HStack style={styles.footer} space={theme.spacing[4]}>
          <Button
            size="small"
            style={styles.button}
            variant="secondary"
            onPress={handleDeny}
          >
            <Text color="textPrimaryInverse">{t('denyButton')}</Text>
          </Button>

          <Button
            size="small"
            style={styles.button}
            variant="secondary"
            onPress={handlePrimaryButtonPress}
          >
            <Text color="textPrimaryInverse">
              {selectedTab === 'details'
                ? t('allowSelection')
                : isNorthAmerica
                  ? t('manage')
                  : t('accept')}
            </Text>
          </Button>

          {!isNorthAmerica && (
            <Button
              size="small"
              style={styles.button}
              variant="secondary"
              onPress={handleAllowAll}
            >
              <Text color="textPrimaryInverse">{t('allowAllButton')}</Text>
            </Button>
          )}
        </HStack>
      </VStack>
    </Animated.View>
  );
};

CookieConsent.displayName = 'CookieConsent';

export { CookieConsent };
